import React, { useState, useMemo, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import { SENSOR_TYPES, MET_CHOICES, DATE_AXIS_TICK_FORMAT_STOPS, toEasternPlotDate } from '../../../utils/sensorConfig';
import { buildArrowBaselineTimeSeries, buildCompassRose, computeArrowResolution } from '../../../utils/windVectorUtils';
import { bucketMinutesForRange } from '../../../hooks/useWaterQualityData';

// Derived daily-total precipitation metric, computed client-side from the
// hourly grid accumulation (precip_accum_1hr_grid) rather than fetched.
const PRECIP_24H_KEY = 'precip_accum_24hr_grid';
const PRECIP_1H_KEY = 'precip_accum_1hr_grid';

// Direction series and the speed series that weights its arrows. Pair at the
// same height: wind_direction_grid is 10m, so it takes wind_speed_grid — using
// the 2m speed would under-scale every arrow by roughly a quarter.
const DIRECTION_MAG_PAIRS = {
  wind_direction_true: 'wind_speed',
  wind_direction_ext:  'wind_speed_ext',   // retired upstream; harmless if absent
  wind_direction_grid: 'wind_speed_grid',
};


/**
 * X-axis config for the metric chart.
 *
 * The daily precip series is one point per day, so the sub-daily entries in
 * DATE_AXIS_TICK_FORMAT_STOPS add a time row under every date label that means
 * nothing for a daily total. Force date-only labels for that kind, and place
 * ticks on the bar x-values themselves (`tickmode: 'array'`) so each date sits
 * centered under its bar — dtick/tick0 arithmetic would drift, since these dates
 * are already shifted by toEasternPlotDate.
 *
 * `days` is the bar x-values; ticks are thinned on long ranges so a month
 * doesn't print 30 overlapping labels.
 */
const metXAxis = (selectedKind, xRange, days = []) => {
  if (selectedKind !== PRECIP_24H_KEY) {
    return {
      type: 'date',
      automargin: true,
      tickformatstops: DATE_AXIS_TICK_FORMAT_STOPS,
      ...(xRange && { range: xRange }),
    };
  }

  const step = Math.max(1, Math.ceil(days.length / 12));
  return {
    type: 'date',
    automargin: true,
    tickformat: '%b %-d',
    hoverformat: '%b %-d',
    tickmode: 'array',
    tickvals: days.filter((_, i) => i % step === 0),
    ...(xRange && { range: xRange }),
  };
};

export default function MetChart({ transformedData, rangeKey, rangeLabel, loadingPriority2, xRange }) {
  const available = useMemo(() => {
    const base = MET_CHOICES.filter(t => transformedData[t]?.length);
    // Offer the derived 24h precip bars whenever hourly grid precip is present.
    if (transformedData[PRECIP_1H_KEY]?.length) base.push(PRECIP_24H_KEY);
    return base;
  }, [transformedData]);

  const [selected, setSelected] = useState(() => available[0] || 'wind_speed');
  const activeSensor = available.includes(selected) ? selected : (available[0] || selected);

  const [arrowResMin, setArrowResMin] = useState(() => bucketMinutesForRange(rangeKey));
  const rangeKeyRef = useRef(rangeKey);
  rangeKeyRef.current = rangeKey;

  const isDirectionSensor = activeSensor in DIRECTION_MAG_PAIRS;

  const vectorData = useMemo(() => {
    if (!isDirectionSensor) return null;
    return buildArrowBaselineTimeSeries(
      transformedData,
      activeSensor,
      DIRECTION_MAG_PAIRS[activeSensor],
      { resolutionMin: arrowResMin, applyCalm: true }
    );
  }, [transformedData, activeSensor, isDirectionSensor, arrowResMin]);

  const isDaily24hPrecip = activeSensor === PRECIP_24H_KEY;

  // Sum hourly grid precip into per-day totals (bucketed by Eastern calendar
  // day so the bars align with the date axis), rendered as one bar per day.
  const barData = useMemo(() => {
    if (!isDaily24hPrecip) return null;
    const buckets = new Map(); // day-midnight ms -> { date, total }
    (transformedData[PRECIP_1H_KEY] || []).forEach(r => {
      const d = toEasternPlotDate(r.time);
      if (isNaN(d)) return;
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const key = day.getTime();
      const entry = buckets.get(key) || { date: day, total: 0 };
      entry.total += Number(r.sensor_value) || 0;
      buckets.set(key, entry);
    });
    const days = [...buckets.values()].sort((a, b) => a.date - b.date);
    return [{
      x: days.map(d => d.date), y: days.map(d => d.total),
      type: 'bar',
      marker: { color: SENSOR_TYPES[PRECIP_24H_KEY]?.color },
      name: SENSOR_TYPES[PRECIP_24H_KEY]?.name,
      hovertemplate: `%{x|%b %-d}<br>%{y:.2f} ${SENSOR_TYPES[PRECIP_24H_KEY]?.unit || ''}<extra></extra>`,
    }];
  }, [transformedData, isDaily24hPrecip]);

  const lineData = useMemo(() => {
    if (vectorData || isDaily24hPrecip) return null;
    const raw = (transformedData[activeSensor] || []).slice().reverse();
    const chartData = raw.map(r => ({ x: toEasternPlotDate(r.time), y: r.sensor_value || 0 }));
    return [{
      x: chartData.map(d => d.x), y: chartData.map(d => d.y),
      type: 'scatter', mode: 'lines',
      line: { color: SENSOR_TYPES[activeSensor]?.color, width: 2 },
      name: SENSOR_TYPES[activeSensor]?.name,
      hovertemplate: `%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${SENSOR_TYPES[activeSensor]?.unit || ''}<extra></extra>`,
    }];
  }, [transformedData, activeSensor, vectorData, isDaily24hPrecip]);

  const onRelayout = useCallback((ev) => {
    const x0 = ev?.['xaxis.range[0]'] || ev?.['xaxis.range']?.[0];
    const x1 = ev?.['xaxis.range[1]'] || ev?.['xaxis.range']?.[1];
    if (x0 && x1) {
      const ms = new Date(x1) - new Date(x0);
      setArrowResMin(prev => Math.min(computeArrowResolution(ms), bucketMinutesForRange(rangeKeyRef.current)));
    }
  }, []);

  const compass = buildCompassRose('tr');
  const st = SENSOR_TYPES[activeSensor] || {};
  const hasData = available.length > 0;
  const precipDays = barData?.[0]?.x ?? [];

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.03)', border: '1px solid rgba(0,0,0,.04)', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
          Meteorological Trends (Past {rangeLabel})
        </h3>
        {loadingPriority2 && (
          <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#64748b', animation: 'spin 0.8s linear infinite' }} />
        )}
      </div>

      {/* Loading state — data fetch still in progress */}
      {!hasData && loadingPriority2 && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          Loading meteorological data…
        </div>
      )}

      {/* Empty state — fetch finished but no sensors returned data */}
      {!hasData && !loadingPriority2 && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          No meteorological data available for this mooring in the selected time range.
        </div>
      )}

      {/* Chart — data available */}
      {hasData && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {available.map(t => (
              <button key={t} onClick={() => setSelected(t)} style={{
                padding: '6px 14px', borderRadius: 100, fontSize: 13, cursor: 'pointer',
                fontWeight: activeSensor === t ? 600 : 400,
                background: activeSensor === t ? '#1e293b' : '#fff',
                color: activeSensor === t ? '#fff' : '#64748b',
                border: activeSensor === t ? 'none' : '1px solid #e2e8f0',
              }}>{SENSOR_TYPES[t]?.name || t}</button>
            ))}
          </div>

          {vectorData ? (
            <Plot
              data={vectorData.traces}
              layout={{
                height: 320,
                xaxis: metXAxis(activeSensor, xRange),
                yaxis: { showticklabels: false, range: [0, 2], fixedrange: true },
                annotations: [...vectorData.annotations, ...compass.annotations],
                shapes: [...compass.shapes, ...(vectorData.baselineShape ? [vectorData.baselineShape] : [])],
                margin: { l: 40, r: 40, t: 20, b: 80 },
                showlegend: false, hovermode: 'closest',
                paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
              }}
              onRelayout={onRelayout}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%' }}
            />
          ) : (
            <Plot
              data={barData || lineData}
              layout={{
                height: 320, margin: { b: 80 },
                xaxis: metXAxis(activeSensor, xRange, precipDays),
                yaxis: { title: { text: `${st.name || activeSensor}${st.unit ? ` (${st.unit})` : ''}` }, automargin: true, rangemode: 'tozero' },
                bargap: 0.2,
                paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                // Bars want per-bar 'closest' hover; the line series reads better
                // with a shared 'x unified' label.
                hovermode: barData ? 'closest' : 'x unified',
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%' }}
            />
          )}

          {activeSensor.endsWith('_ext') && (
            <p style={{ fontSize: 12, color: '#546e7a', textAlign: 'right', marginTop: 8 }}>
              • Variables with <strong>ext</strong> are sourced from external weather stations.
            </p>
          )}
          {activeSensor.endsWith('_grid') && (
            <p style={{ fontSize: 12, color: '#546e7a', textAlign: 'right', marginTop: 8 }}>
              • Variables with <strong>grid</strong> are sourced from gridded RTMA/HRRR/MRMS models.
            </p>
          )}
        </>
      )}
    </div>
  );
}